import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';

import { CIBuildNumber } from '@Constants/CommonConstants';
import { LinkElement } from '@els/els-react--link-element';
import Page from '@Containers/Page';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import RouteChangeTracking from '@Services/RouteChangeTracking';

import './AboutContent.scss';

const spinnerId = 'aboutContentSpinner';

function AboutContent(props) {
  const { isLoading, init, BEBuildNumber, updateInfo, databases } = props;
  const aboutSuppliersUrl = 'https://service.elsevier.com/app/answers/detail/a_id/38689/supporthub/reaxys/';

  useEffect(() => {
    init();
    RouteChangeTracking.handleSkipLinkFocus();
    // eslint-disable-next-line
  }, []);

  const formatDate = (date) => (date ? moment(date).format('DD. MMMM  YYYY') : null);

  // eslint-disable-next-line sonarjs/slow-regex
  const formatNumber = (number) => String(number).replace(/(.)(?=(\d{3})+$)/g, '$1 ');

  return (
    <Page
      withHeader
      headerType="clean"
      withFooter
      pageClassName="react-page-static about-content-page"
      className="react-page-static__wrapper">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div id="main-content" tabIndex="0">
        <div className="about-content-page__header rx-left-gutter rx-right-gutter">
          <h1 className="about-content-page__header_title">About Reaxys</h1>
          <div className="about-content-page__header_info">
            <p>
              Reaxys is a unique web-based chemistry database consisting of deeply excerpted compounds and related factual properties,
              reaction and synthesis information as well as bibliographic data, navigated and displayed via an actionable interface. <br />
              Please visit
              <a
                className="u-els-color-link-inline"
                tabIndex="0"
                target="_blank"
                // eslint-disable-next-line sonarjs/no-clear-text-protocols
                href="http://www.elsevier.com/solutions/reaxys?dgcid=RN_AGCM_Sourced_300005028"
                rel="noreferrer">
                http://www.elsevier.com/solutions/reaxys
              </a>
              to learn more about Reaxys.
            </p>
            <p>The Reaxys website is owned and operated by Elsevier Information Systems GmbH.</p>
          </div>
        </div>
        <div className="about-content-page__content rx-left-gutter rx-right-gutter">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          {!isEmpty(updateInfo) && (
            <div className="about-content-page__content_updated">
              <span className="about-content-page__content_title">Content added to Reaxys since last update:</span>
              <span>
                {updateInfo.map((item, index) => (
                  <span key={`about-content-updated-${item.name}`} className="about-content-page__content_value">
                    {formatNumber(item.value) || 'multiple'} {item.name + (index === updateInfo.length - 1 ? '' : ', ')}
                  </span>
                ))}
              </span>
            </div>
          )}

          <ul className="about-content-page__content_list">
            {databases.map((database) => {
              if (!database || !database.lastUpdated) {
                return null;
              }
              return (
                <li key={`about-content-list-item-${database.id}`} className="about-content-page__content_list_item">
                  <div>
                    <span className="about-content-page__content_title">{database.aboutContentDbSectionLabel}</span>
                  </div>
                  <div>
                    <span className="about-content-page__content_label">Last updated: </span>
                    <span className="about-content-page__content_value">{formatDate(database.lastUpdated)}</span>
                  </div>
                  {get(database, 'availableContexts', []).map((context) => (
                    <div
                      key={`about-content-list-info-${database.id}-${context.displayName}`}
                      className="about-content-page__content_list_item_context">
                      <span className="about-content-page__content_label">{context.displayName}: </span>
                      {context.displayName.toLowerCase() === 'suppliers' ? (
                        <LinkElement
                          href={aboutSuppliersUrl}
                          isExternal
                          noExternalIcon
                          a11y="Click here for more information about Suppliers">
                          Click here for more information
                        </LinkElement>
                      ) : (
                        <span className="about-content-page__content_value">{formatNumber(context.entriesCount)}</span>
                      )}
                    </div>
                  ))}
                </li>
              );
            })}
          </ul>

          <div className="about-content-page__content_versions">
            <h2 className="about-content-page__content_versions_title">Versions</h2>
            <div>
              <span className="about-content-page__content_label">FE Version: </span>
              <span className="about-content-page__content_value">{CIBuildNumber}</span>
            </div>
            <div>
              <span className="about-content-page__content_label">BE Version: </span>
              <span className="about-content-page__content_value">{BEBuildNumber}</span>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

AboutContent.propTypes = {
  init: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  BEBuildNumber: PropTypes.string,
  updateInfo: PropTypes.array,
  databases: PropTypes.array,
};

export default AboutContent;
